import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProjectProvider, useProject } from './context/ProjectContext';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './pages/DashboardLayout';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import Dashboard from './pages/Dashboard';
import SiteAudit from './pages/SiteAudit';
import KeywordAnalysis from './pages/KeywordAnalysis';
import CompetitorAnalysis from './pages/CompetitorAnalysis';
import RankTracking from './pages/RankTracking';
import CoreWebVitals from './pages/CoreWebVitals';
import AccountSettings from './pages/AccountSettings';
import Integrations from './pages/Integrations';
import ParrotAutoRank from './pages/ParrotAutoRank';
import ProjectSetupWizard from './components/ProjectSetupWizard';
import NotFound from './pages/NotFound';
import AuthCallback from './pages/AuthCallback';
import InternetCheck from './components/InternetCheck';
import PageLoader from './components/PageLoader';
import './App.css';

// Inner component that has access to context
function AppContent() {
  const { loading: authLoading } = useAuth();
  const { loading: projectLoading } = useProject();
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    // Show loading screen during initial app load
    if (!authLoading && !projectLoading) {
      // Small delay to ensure smooth transition
      const timer = setTimeout(() => {
        setInitialLoad(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [authLoading, projectLoading]);

  // Show loading screen during initial load or when contexts are loading
  if (initialLoad || authLoading || projectLoading) {
    return <PageLoader forceShow={true} />;
  }

  return (
    <InternetCheck>
      <div className="App">
        <Routes>
                <Route path="/signin" element={<SignIn />} />
                <Route path="/signup" element={<SignUp />} />
                <Route
                  path="/project-setup"
                  element={
                    <ProtectedRoute>
                      <ProjectSetupWizard onComplete={() => window.location.href = '/dashboard'} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <DashboardLayout>
                        <Dashboard />
                      </DashboardLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/site-audit"
                  element={
                    <ProtectedRoute>
                      <DashboardLayout>
                        <SiteAudit />
                      </DashboardLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/keyword-analysis"
                  element={
                    <ProtectedRoute>
                      <DashboardLayout>
                        <KeywordAnalysis />
                      </DashboardLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/competitor-analysis"
                  element={
                    <ProtectedRoute>
                      <DashboardLayout>
                        <CompetitorAnalysis />
                      </DashboardLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/rank-tracking"
                  element={
                    <ProtectedRoute>
                      <DashboardLayout>
                        <RankTracking />
                      </DashboardLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/core-web-vitals"
                  element={
                    <ProtectedRoute>
                      <DashboardLayout>
                        <CoreWebVitals />
                      </DashboardLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/account-settings"
                  element={
                    <ProtectedRoute>
                      <DashboardLayout>
                        <AccountSettings />
                      </DashboardLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/integrations"
                  element={
                    <ProtectedRoute>
                      <DashboardLayout>
                        <Integrations />
                      </DashboardLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard/integrations/parrot-seo-ai-engine"
                  element={
                    <ProtectedRoute>
                      <DashboardLayout>
                        <ParrotAutoRank />
                      </DashboardLayout>
                    </ProtectedRoute>
                  }
                />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/" element={<Navigate to="/signin" replace />} />
                <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </InternetCheck>
  );
}

function App() {
  return (
    <AuthProvider>
      <ProjectProvider>
        <Router>
          <AppContent />
        </Router>
      </ProjectProvider>
    </AuthProvider>
  );
}

export default App;
